import { useEffect, useRef, useState } from 'react'
import type ExperienceData from '../../types/experience'
import { getAssetUrl } from '../../utils'
import Container from '../Container'
import { OverlayButton } from '../OverlayButton'
import { OverlayLogo } from '../OverlayLogo'
import { ASSETS } from '../../assets'
import { audio } from '../../utils/audio'

interface Props {
  mode: 'driver' | 'engineer'
  experience: ExperienceData
  hasClickedOnce: boolean
  isAnimating: boolean
  isInActivePOI: boolean
  onLogoClick?: () => void
  onStart?: () => void
}
export default function EducatePanel({
  mode,
  experience,
  hasClickedOnce,
  isAnimating,
  isInActivePOI,
  onLogoClick,
  onStart,
}: Props) {
  const logo = experience.brandLogo
    ? experience.brandLogo.url.startsWith('blob:')
      ? experience.brandLogo.url
      : getAssetUrl(experience.brandLogo.filename)
    : ASSETS.ibm
  const background = experience.colours?.exploreLeftPanelBackground || '#002D9C'

  const panelData =
    mode === 'engineer' ? experience.engineerMode.educatePanel : experience.driverMode.educatePanel
  const startCtaRef = useRef<HTMLButtonElement>(null)
  const logoRef = useRef<HTMLImageElement>(null)

  const [showOverlayLogo, setShowOverlayLogo] = useState(false)
  useEffect(() => {
    setShowOverlayLogo(isInActivePOI && !isAnimating)
  }, [isInActivePOI, isAnimating])

  const [showOverlayButton, setShowOverlayButton] = useState(false)
  useEffect(() => {
    setShowOverlayButton(isInActivePOI && hasClickedOnce && !isAnimating)
  }, [isInActivePOI, hasClickedOnce, isAnimating])

  return (
    <>
      <div className="absolute top-0 left-0 z-0 h-full w-full">
        <Container>
          <div className="absolute inset-0 flex h-full w-full py-[61px] pl-[66px]">
            <div
              className="bg-blue flex h-full w-[537px] shrink-0 flex-col justify-between px-10 py-[45.87px]"
              style={{ background: `${background}` }}
            >
              <div className="flex flex-col gap-16">
                <div className="flex items-center justify-between">
                  <img ref={logoRef} src={logo} width={118} height={44} onClick={onLogoClick} />
                  <span className="block max-w-2/5 text-[23px] leading-[30px] font-light">
                    {experience.slogan || 'watsonx. Your AI Race Engineer'}
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-[106px]">
                <button
                  ref={startCtaRef}
                  disabled={!hasClickedOnce || isAnimating}
                  className={`flex items-center justify-between gap-40 bg-transparent px-6 py-3 text-left text-[20px] leading-[27px] font-light text-transparent ${!hasClickedOnce ? 'cursor-not-allowed opacity-50' : ''}`}
                >
                  <span>{panelData?.startCtaLabel || 'Hit the Track'}</span>
                </button>
              </div>
            </div>
          </div>
        </Container>
      </div>

      {showOverlayLogo && (
        <OverlayLogo targetRef={logoRef} onClick={() => onLogoClick?.()} logo={logo} />
      )}
      {showOverlayButton && (
        <OverlayButton
          targetRef={startCtaRef}
          onClick={() => {
            const sfx = experience.soundEffects?.mainButton
            if (sfx) {
              const url = sfx.url.startsWith('data:') ? sfx.url : getAssetUrl(sfx.filename)
              if (url) {
                audio.register({ mainButton: url })
                audio.play('mainButton')
              }
            }
            onStart?.()
          }}
          label={panelData?.startCtaLabel || 'Hit the Track'}
        />
      )}
    </>
  )
}
